import React, { useState, useEffect, useCallback } from 'react';
import * as Progress from '@radix-ui/react-progress';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import ButtonExitModal from './ButtonExitModal';

function GenerateReportProgressDialog({ 
  reportStage, 
  patientId, 
  clinicianId, 
  handleNextPageButtonClick,
  isGenerating,
  onCancel,
  completionButtonLabel,
}) {
const [currentStep, setCurrentStep] = useState(0);
const [progressValues, setProgressValues] = useState([0, 0, 0]);
const [isProcessing, setIsProcessing] = useState(false);

const handleCancel = useCallback(() => {
  setProgressValues([0, 0, 0]);
  setCurrentStep(0);
  setIsProcessing(false);
  onCancel();
}, [onCancel]);

const generateReport = useCallback(async () => {
  if (!isGenerating || isProcessing) return;

  setIsProcessing(true);
  // const GenerateDraftWithLLM = httpsCallable(functions, 'GenerateDraftWithLLM');

  try {
    // const reportParams = {
    //   clinicianId,
    //   patientId,
    //   reportStage,
    //   reportSection: 'All',
    //   taskType: "Generate Full Document",
    //   isTest: false,
    // };

    // await GenerateDraftWithLLM(reportParams);
    
    // First step progress
    setProgressValues([0, 0, 0]);
    await new Promise(resolve => {
      const timer1 = setTimeout(() => setProgressValues([30, 0, 0]), 500);
      const timer2 = setTimeout(() => setProgressValues([50, 0, 0]), 1000);
      const timer3 = setTimeout(() => setProgressValues([65, 0, 0]), 2000);
      const timer4 = setTimeout(() => {
        setProgressValues([100, 0, 0]);
        setCurrentStep(1);
        resolve();
      }, 3500);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
        clearTimeout(timer4);
      };
    });

    // Second step progress
    await new Promise(resolve => {
      const timer1 = setTimeout(() => setProgressValues([100, 30, 0]), 500);
      const timer2 = setTimeout(() => setProgressValues([100, 65, 0]), 1000);
      const timer3 = setTimeout(() => setProgressValues([100, 90, 0]), 2000);
      const timer4 = setTimeout(() => {
        setProgressValues([100, 100, 0]);
        setCurrentStep(2);
        resolve();
      }, 3500);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
        clearTimeout(timer4);
      };
    });

    // Third step progress
    await new Promise(resolve => {
      const timer1 = setTimeout(() => setProgressValues([100, 100, 10]), 500);
      const timer2 = setTimeout(() => setProgressValues([100, 100, 70]), 1000);
      const timer3 = setTimeout(() => setProgressValues([100, 100, 90]), 2000);
      const timer4 = setTimeout(() => {
        setProgressValues([100, 100, 100]);
        resolve();
      }, 3500);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
        clearTimeout(timer4);
      };
    });

  } catch (error) {
    console.error('Error generating report:', error);
    handleCancel();
  }
}, [
  isGenerating, 
  isProcessing,
  // clinicianId, 
  // patientId, 
  // reportStage, 
  handleCancel
]);

useEffect(() => {
  let mounted = true;

  if (mounted && isGenerating && !isProcessing && progressValues[2] !== 100) {
    generateReport();
  }

  return () => {
    mounted = false;
  };
}, [isGenerating, generateReport, isProcessing, progressValues]);

const isComplete = progressValues[2] === 100;

  return (
    <AlertDialog.Root open={isGenerating}>     
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/60" />
        <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg p-6 w-[400px]">
          <div className="flex justify-between items-start">
          <AlertDialog.Title className="text-lg font-semibold mb-4">
            {reportStage} Report
          </AlertDialog.Title>
          <ButtonExitModal text="Cancel"/>
          </div>
          <AlertDialog.Description className="text-sm text-gray-600 mb-4">
            Please wait while the {reportStage} Report is prepared.
          </AlertDialog.Description>

          <div className="space-y-6">
            {/* Step 1 */}
            <div className="w-full">
              <div className="flex justify-between mb-2">
                <span className="text-sm">Fetching relevant information sources...</span>
                {currentStep > 0 && <span className="text-green-500 text-xs">✔</span>}
              </div>
              <Progress.Root 
                className="h-2 w-full bg-gray-200 rounded-full overflow-hidden"
                value={progressValues[0]}
              >
                <Progress.Indicator
                  className={`h-full rounded-full transition-transform duration-[300ms] ease-in-out ${
                    progressValues[0] === 100 ? 'bg-green-600' : 'bg-violet-600'
                  }`}
                  style={{ transform: `translateX(-${100 - progressValues[0]}%)` }}
                />
              </Progress.Root>
            </div>

            {/* Step 2 */}
            <div className="w-full">
              <div className="flex justify-between mb-2">
                <span className="text-sm">Writing draft document...</span>
                {currentStep > 1 && <span className="text-green-500 text-xs">✔</span>}
              </div>
              <Progress.Root 
                className="h-2 w-full bg-gray-200 rounded-full overflow-hidden"
                value={progressValues[1]}
              >
                <Progress.Indicator
                  className={`h-full rounded-full transition-transform duration-[300ms] ease-in-out ${
                    progressValues[1] === 100 ? 'bg-green-600' : 'bg-violet-600'
                  }`}
                  style={{ transform: `translateX(-${100 - progressValues[1]}%)` }}
                />
              </Progress.Root>
            </div>

            {/* Step 3 */}
            <div className="w-full">
              <div className="flex justify-between mb-2">
                <span className="text-sm">Preparing draft document for clinician review...</span>
                {isComplete && <span className="text-green-500 text-xs">✔</span>}
              </div>
              <Progress.Root 
                className="h-2 w-full bg-gray-200 rounded-full overflow-hidden"
                value={progressValues[2]}
              >
                <Progress.Indicator
                  className={`h-full rounded-full transition-transform duration-[300ms] ease-in-out ${
                    progressValues[2] === 100 ? 'bg-green-600' : 'bg-violet-600'
                  }`}
                  style={{ transform: `translateX(-${100 - progressValues[2]}%)` }}
                />
              </Progress.Root>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between space-x-3 mt-6">
              {!isComplete && (
                <AlertDialog.Cancel asChild>
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  >
                    Cancel
                  </button>
                </AlertDialog.Cancel>
              )}
              <div className={!isComplete ? "" : "ml-auto"}>
                <AlertDialog.Action asChild>
                  <button
                    onClick={() => handleNextPageButtonClick()}
                    disabled={!isComplete}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${
                      isComplete 
                        ? 'bg-violet-800 hover:bg-violet-700' 
                        : 'bg-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {isComplete ? (completionButtonLabel || "→ Go to Review") : "Generating..."}
                  </button>
                </AlertDialog.Action>
              </div>
            </div>
        </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

export default GenerateReportProgressDialog;